const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  media: '[data-product-media]',
  price: '[data-product-price]',
  backInStockTrigger: '[data-product-back-in-stock-trigger]',
  colourLink: '[data-product-colour-link]',
}

export class ProductInfo extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form']
  }

  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  /** @type {AbortController | undefined} */
  abortController = undefined

  /** @type {string | undefined} */
  pendingRequestUrl = undefined

  /** @type {Function[]} */
  preReplaceProductOptionsCallbacks = []

  /** @type {Function[]} */
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()

    if (this.els.form.element.tagName !== 'PRODUCT-FORM') {
      this.throwError(`this.els.form.element should be a <product-form>`)
    }
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)
    this.onColourLinkClick = this.onColourLinkClick.bind(this)
    this.handlePopState = this.handlePopState.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    this.addEventListener('click', this.onColourLinkClick)
    window.addEventListener('popstate', this.handlePopState)
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    this.removeEventListener('click', this.onColourLinkClick)
    window.removeEventListener('popstate', this.handlePopState)
  }

  /**
   * Pre-fetches product data when an option is "entered" (e.g. hovered/focused), to warm the cache.
   *
   * @param {CustomEvent} payload
   * @param {{ event: Event, selectedOptionValues: string[] }} payload.detail
   * @returns {void}
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId
      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * Handles option selection changes for actual product options.
   *
   * @param {CustomEvent} payload
   * @param {{ event: Event, target: (HTMLOptionElement|EventTarget), selectedOptionValues: string[] }} payload.detail
   * @returns {Promise<void>}
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    if (!this.contains(target)) return
    if (this.dataset.productInfoId !== target.dataset.productInfoId) return

    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl

    const shouldSwapProduct = this.dataset.url !== productUrl

    // Original behaviour retained (if your theme uses this to force a full page swap).
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   * @returns {void}
   */

  /**
   * Fetches product data (HTML), parses it, then calls the provided callback with the parsed Document.
   * Aborts any in-flight request before starting a new one.
   *
   * @param {Object} payload
   * @param {string} payload.requestUrl - Fully qualified request URL (including section_id / option_values params).
   * @param {string} payload.targetId - Element id to restore focus to after update (optional).
   * @param {fetchProductDetailsCallback} payload.callback - Called with parsed HTML document on success.
   * @returns {Promise<void>}
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const text = await fetchProductData(
        requestUrl,
        this.abortController.signal,
      )

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      // Avoid invalid selector '#'
      if (targetId) {
        window.BAO.Utils.getElement(`#${targetId}`)?.focus()
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)
      }
    }
  }

  /**
   * Handles clicks on related product colour links.
   * Intercepts normal navigation and performs an AJAX product swap instead.
   *
   * - Uses pushState so colour changes are added to browser history.
   * - Does NOT include ?variant= in the URL (clean product URL).
   *
   * @param {MouseEvent} event
   * @returns {Promise<void>}
   */
  async onColourLinkClick (event) {
    /** @type {HTMLAnchorElement|null} */
    const link = event.target.closest(selectors.colourLink)
    if (!link) return

    if (!this.contains(link)) return

    // Preserve native behaviours
    if (event.defaultPrevented) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }
    if (link.target && link.target !== '_self') return

    event.preventDefault()

    // Already active colour? Do nothing.
    if (link.classList.contains('prd-Options_ColourLink-active')) return

    /** @type {string|null} */
    const productUrl = link.dataset.productUrl || link.getAttribute('href')
    if (!productUrl) return

    this.loading = true
    this.pendingRequestUrl = productUrl

    const shouldFetchFullPage = false

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        [],
        shouldFetchFullPage,
      ),

      // Optional focus restore - safe due to guard in fetchProductDetails
      targetId: link.id || '',

      // push history + swap product, but keep URL clean (no ?variant=)
      callback: this.handleSwapProduct(
        productUrl,
        shouldFetchFullPage,
        'push',
        false,
      ),
    })
  }

  /**
   * Creates a callback that swaps either the whole page (main) or just <product-info>
   * using fetched HTML. Also updates browser history / share buttons.
   *
   * @param {string} productUrl - Base product URL to set in the address bar.
   * @param {boolean} [updateFullPage=false] - If true, swaps <main>; otherwise swaps <product-info>.
   * @param {'push'|'replace'} [historyMode='replace'] - History API method to use.
   * @param {boolean} [includeVariantInUrl=true] - If false, removes ?variant= from the URL update.
   * @returns {(html: Document) => void}
   */
  handleSwapProduct (
    productUrl,
    updateFullPage = false,
    historyMode = 'replace',
    includeVariantInUrl = true,
  ) {
    return (html) => {
      const variant = this.getSelectedVariant(html)

      const variantIdForUrl = includeVariantInUrl ? variant?.id : undefined
      this.updateURL(productUrl, variantIdForUrl, historyMode)

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        window.BAO.Utils.gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      } else {
        window.BAO.Utils.gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      }
    }
  }

  /**
   * Handles browser back/forward navigation.
   * Swaps product-info to match the current URL without writing to history.
   *
   * @param {PopStateEvent} event
   * @returns {void}
   */
  handlePopState (event) {
    const productInfoEl = document.querySelector('product-info')
    if (!productInfoEl || typeof productInfoEl.swapToUrl !== 'function') return

    productInfoEl.swapToUrl(window.location.href)
  }

  /**
   * Swaps product-info to match the given URL, without modifying history.
   * Used for handling popstate (back/forward).
   *
   * @param {string} url - URL to fetch and render (can include query params).
   * @returns {Promise<void>}
   */
  async swapToUrl (url) {
    const shouldFetchFullPage = false

    this.loading = true
    this.pendingRequestUrl = url

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(url, [], shouldFetchFullPage),
      targetId: '',
      callback: (html) => {
        // IMPORTANT: do NOT call updateURL here (browser already changed it)
        window.BAO.Utils.gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      },
    })

    this.loading = false
  }

  /**
   * Renders updates for the currently selected variant (same product),
   * updating media/price/availability etc. without swapping the whole product-info node.
   *
   * @param {string} productUrl
   * @returns {(html: Document) => void}
   */
  renderProductDetails (productUrl) {
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (this.productOptions && !variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)
        this.loading = false
        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id) // replaceState by default
      this.updateVariantInputs(variant?.id)
      this.updateWishlistButtons(variant?.id)
      this.updateBackInStockTriggers(variant)

      /**
       * @callback PostReplaceCallback
       * @param {Element[]} currentElements
       * @param {Element} newElement
       * @returns {void}
       */

      /**
       * Updates matching elements on the current page with the fetched HTML.
       *
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       * @returns {void}
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = window.BAO.Utils.getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
      )

      const productIsAvailable = !html
        .querySelector(`product-info product-form button[type=submit]`)
        ?.hasAttribute('disabled')

      this.updateAvailability(productIsAvailable)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        html,
        variant,
      })

      this.loading = false
    }
  }

  updateBackInStockTriggers (variant) {
    if (!variant) return

    const backInStockTriggerEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
    )

    if (!backInStockTriggerEls?.length) return

    backInStockTriggerEls.forEach((triggerEl) => {
      triggerEl.dataset.variantId = variant?.id || ''
      triggerEl.dataset.variantTitle = variant?.title || ''
    })
  }

  /**
   * Updates add-to-cart button state + text based on availability.
   *
   * @param {boolean} productIsAvailable
   * @param {string} [text]
   * @returns {void}
   */
  updateAvailability (productIsAvailable, text) {
    const addToCartEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )
    const isComingSoon = addToCartEls.some(
      (addToCartEl) =>
        addToCartEl.dataset.hasCountdown === 'true' &&
        addToCartEl.dataset.notifyMeEnabled === 'false',
    )
    const addToCartTextEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartText}`,
    )
    const backInStockTriggerEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
    )

    addToCartEls.forEach((addToCartEl) => {
      const hideUnavailableButton =
        !productIsAvailable && addToCartEl.dataset.notifyMeEnabled !== 'false'

      addToCartEl.toggleAttribute('disabled', !productIsAvailable)
      addToCartEl.setAttribute('aria-hidden', hideUnavailableButton)
    })

    let addToCartText = window.theme.strings.soldOut
    if (isComingSoon) {
      addToCartText = window.theme.strings.comingSoon
    } else if (text || productIsAvailable) {
      addToCartText = window.theme.strings.addToCart
    }

    addToCartTextEls.forEach((addToCartTextEl) => {
      addToCartTextEl.textContent = addToCartText
    })

    backInStockTriggerEls.forEach((backInStockTriggerEl) => {
      backInStockTriggerEl.setAttribute('aria-hidden', productIsAvailable)
    })
  }

  /**
   * Replaces the product options DOM block with the fetched one.
   *
   * @param {Document} html
   * @returns {void}
   */
  updateOptionValues (html) {
    const productOptionsAll = html.querySelectorAll(
      `${this.productOptions.tagName.toLowerCase()}[data-product-info-id="${this.productInfoId}"]`,
    )

    if (productOptionsAll) {
      productOptionsAll.forEach((productOption) => {
        /**
         * Add data-open to options accordion so it renders open then close it
         * immediately to give the impression of seamless animation
         */
        const optionsAccordion =
          productOption.querySelector('options-accordion')

        if (optionsAccordion) {
          optionsAccordion.dataset.open = true
        }

        window.BAO.Utils.gracefullyReplaceNode(
          this.querySelector(`product-options#${productOption.id}`),
          productOption,
          this.preReplaceProductOptionsCallbacks,
        )
      })
    }
  }

  /**
   * Updates the browser URL (and share buttons) without a full page reload.
   *
   * @param {string} url - Base URL (e.g. /products/handle or absolute).
   * @param {string} [variantId] - Variant id to set/remove in query string.
   * @param {'push'|'replace'} [mode='replace'] - Whether to push or replace history state.
   * @returns {void}
   */
  updateURL (url, variantId, mode = 'replace') {
    if (this.dataset.updateUrl === 'false') return

    const next = new URL(url, window.location.origin)

    if (variantId) next.searchParams.set('variant', variantId)
    else next.searchParams.delete('variant')

    const nextUrl = next.toString()

    if (mode === 'push') {
      window.history.pushState({ url: nextUrl, variantId }, '', nextUrl)
    } else {
      window.history.replaceState({ url: nextUrl, variantId }, '', nextUrl)
    }

    const shareButtons = window.BAO.Utils.getElements('share-button')
    if (!shareButtons) return
    shareButtons.forEach((shareButton) => shareButton.updateUrl(nextUrl))
  }

  /**
   * Updates hidden variant id input inside the product form and triggers a change event.
   *
   * @param {string} [variantId]
   * @returns {void}
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    this.productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')

      if (!input) return

      input.value = variantId
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  /**
   * Update wishlist button variant ID data attribute.
   *
   *  @param {string} [variantId]
   */
  updateWishlistButtons (variantId) {
    if (!variantId) return

    this.productForms.forEach((productForm) => {
      const wishlistButtonEl = productForm.querySelector(
        '[data-product-wishlist-button]',
      )
      if (!wishlistButtonEl) return
      wishlistButtonEl.dataset.variantId = variantId
    })
  }

  /**
   * Builds a request URL with the appropriate params for section rendering.
   *
   * @param {string} url - Base URL to request (relative or absolute).
   * @param {string[]} optionValues - Option value ids used by your option system.
   * @param {boolean} [shouldFetchFullPage=false] - If true, omit section_id to fetch the full page.
   * @returns {string} Fully-qualified request URL.
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const req = new URL(url, window.location.origin)

    if (!shouldFetchFullPage) {
      req.searchParams.set('section_id', this.sectionId)
    } else req.searchParams.delete('section_id')

    if (optionValues?.length) {
      req.searchParams.set('option_values', optionValues.join(','))
    } else {
      req.searchParams.delete('option_values')
    }

    return req.toString()
  }

  /**
   * Reads the selected variant JSON blob from the fetched HTML.
   *
   * @param {Document} productInfoNode
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    if (!this.productOptions) return null

    const selectedVariantDataEl = productInfoNode.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="selected-variant"]`,
    )
    if (!selectedVariantDataEl) return null

    return JSON.parse(selectedVariantDataEl.innerHTML)
  }

  get productForms () {
    return this.els?.form?.elements
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els?.options?.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {string} key
   * @param {string} value
   * @returns {void}
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {string} key
   * @returns {string|undefined}
   */
  get (key) {
    return this.cache.get(key)
  }

  /**
   * @param {string} key
   * @returns {void}
   */
  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * Fetches HTML for a given request URL (with caching).
 *
 * @param {string} requestUrl
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<string>}
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
